import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

import { createFirstOrganization } from '../api/organizations'
import { formatApiErrorMessage } from '../api/apiErrorMessage'
import type { OrganizationDto } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { useFocusTrap } from '../hooks/useFocusTrap'
import { listCities } from '../api/reference'
import { SelectCombobox } from './SelectCombobox'
import { MarketEmptyState } from './MarketEmptyState'
import {
  hasVenueFormErrors,
  validateVenueForm,
  type VenueFormField,
  type VenueFormFieldErrors,
  type VenueFormValues,
} from '../utils/venueFormValidation'

const emptyValues: VenueFormValues = {
  ogrn: '',
  legalName: '',
  tradeName: '',
  city: 'perm',
  horecaType: 'other',
}

type WorkspaceCopy = {
  title: string
  lead: string
  emptyTitle: string
  emptyDescription: string
  actionLabel: string
}

const workspaceCopyForPath = (pathname: string): WorkspaceCopy => {
  if (pathname.startsWith('/my-prices/import')) {
    return {
      title: 'Загрузка прайса',
      lead: 'Добавьте организацию, чтобы закрепить за ней загружаемые прайсы.',
      emptyTitle: 'Перед загрузкой укажите организацию',
      emptyDescription: 'Создайте карточку организации — это займёт пару минут. После этого вернёмся к загрузке прайса.',
      actionLabel: 'Указать организацию',
    }
  }

  if (pathname.startsWith('/my-prices/suppliers')) {
    return {
      title: 'Мои поставщики',
      lead: 'Здесь будут поставщики, с которыми вы работаете через Провизиум.',
      emptyTitle: 'Перед началом укажите организацию',
      emptyDescription: 'Создайте карточку организации — это займёт пару минут. После этого вернёмся к выбранному разделу.',
      actionLabel: 'Указать организацию',
    }
  }

  if (pathname.startsWith('/analytics')) {
    return {
      title: 'Динамика цен',
      lead: 'Здесь можно будет отслеживать изменения цен по вашим позициям.',
      emptyTitle: 'Чтобы открыть динамику цен, укажите организацию',
      emptyDescription: 'Создайте карточку организации — это займёт пару минут. После этого вернёмся к выбранному разделу.',
      actionLabel: 'Указать организацию',
    }
  }

  if (pathname.startsWith('/purchase')) {
    return {
      title: 'Заказ по остаткам',
      lead: 'Здесь будут рекомендации по закупке на основе остатков и расхода.',
      emptyTitle: 'Чтобы рассчитать закупку, укажите организацию',
      emptyDescription: 'Создайте карточку организации — это займёт пару минут. После этого вернёмся к выбранному разделу.',
      actionLabel: 'Указать организацию',
    }
  }

  if (pathname.startsWith('/cart')) {
    return {
      title: 'Заявка',
      lead: 'Здесь можно будет собрать и отправить заявку поставщику.',
      emptyTitle: 'Чтобы создать заявку, укажите организацию',
      emptyDescription: 'Создайте карточку организации — это займёт пару минут. После этого вернёмся к выбранному разделу.',
      actionLabel: 'Указать организацию',
    }
  }

  if (pathname.startsWith('/settings')) {
    return {
      title: 'Настройки',
      lead: 'После создания организации здесь появятся её настройки и интеграции.',
      emptyTitle: 'Чтобы открыть настройки, укажите организацию',
      emptyDescription: 'Создайте карточку организации — это займёт пару минут. После этого вернёмся к выбранному разделу.',
      actionLabel: 'Указать организацию',
    }
  }

  return {
    title: 'Мои прайсы',
    lead: 'Ваши цены и поставщики будут собраны здесь.',
    emptyTitle: 'Прайсов пока нет',
    emptyDescription: 'Создайте карточку организации — это займёт пару минут. После этого вернёмся к загрузке прайса.',
    actionLabel: 'Загрузить прайс',
  }
}

const Field = ({ error, label, children }: { error?: string; label: ReactNode; children: ReactNode }) => (
  <label className={`echat-field${error ? ' echat-field--invalid' : ''}`}>
    <span className="echat-field__label">{label}</span>
    {children}
    {error ? <span className="echat-field__error" role="alert">{error}</span> : null}
  </label>
)

const FirstOrganizationModal = ({
  open,
  onDismiss,
  onCompleted,
}: {
  open: boolean
  onDismiss: () => void
  onCompleted: (organization: OrganizationDto) => Promise<void>
}) => {
  const dialogRef = useFocusTrap<HTMLDivElement>(open, { onClose: onDismiss })
  const [cities, setCities] = useState<{ code: string; label: string }[]>([])
  const [values, setValues] = useState<VenueFormValues>(emptyValues)
  const [errors, setErrors] = useState<VenueFormFieldErrors>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setValues(emptyValues)
    setErrors({})
    setFormError(null)
    void listCities().then(setCities).catch(() => setCities([]))
  }, [open])

  const clearError = (field: VenueFormField) => {
    setErrors((current) => {
      if (!current[field]) return current
      const next = { ...current }
      delete next[field]
      return next
    })
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const nextErrors = validateVenueForm(values)
    setErrors(nextErrors)
    if (hasVenueFormErrors(nextErrors)) return

    setSubmitting(true)
    setFormError(null)
    try {
      const organization = await createFirstOrganization({
        ogrn: values.ogrn.trim(),
        legalName: values.legalName.trim(),
        tradeName: values.tradeName.trim(),
        city: values.city,
      })
      await onCompleted(organization)
    } catch (error) {
      setFormError(formatApiErrorMessage(error, 'Не удалось создать организацию'))
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) return null

  return (
    <div className="market-add-venue-overlay" role="presentation" onClick={() => !submitting && onDismiss()}>
      <div
        ref={dialogRef}
        className="market-add-venue-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="first-organization-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="first-organization-title" className="market-add-venue-modal__title">Расскажите об организации</h2>
        <p className="market-add-venue-modal__subtitle">Это нужно, чтобы отделить ваши прайсы и закупки. Остальное заполним по ходу работы.</p>
        <form className="market-add-venue-form" noValidate onSubmit={(event) => void submit(event)}>
          <Field error={errors.ogrn} label={<>ОГРН или ОГРНИП <span className="market-auth-field__required">*</span></>}>
            <input className="echat-field__input" inputMode="numeric" placeholder="13 цифр у компании, 15 у ИП" value={values.ogrn} onChange={(event) => { setValues((current) => ({ ...current, ogrn: event.target.value })); clearError('ogrn') }} />
          </Field>
          <Field error={errors.legalName} label={<>Юридическое название <span className="market-auth-field__required">*</span></>}>
            <input className="echat-field__input" placeholder="ООО «Название»" value={values.legalName} onChange={(event) => { setValues((current) => ({ ...current, legalName: event.target.value })); clearError('legalName') }} />
          </Field>
          <Field error={errors.tradeName} label={<>Торговое название <span className="market-auth-field__required">*</span></>}>
            <input className="echat-field__input" placeholder="Например: кафе «Уют»" value={values.tradeName} onChange={(event) => { setValues((current) => ({ ...current, tradeName: event.target.value })); clearError('tradeName') }} />
          </Field>
          <Field error={errors.city} label={<>Город <span className="market-auth-field__required">*</span></>}>
            <SelectCombobox embedded menuPlacement="top" label="Город" value={values.city} options={cities.map((city) => ({ value: city.code, label: city.label }))} onChange={(city) => { setValues((current) => ({ ...current, city })); clearError('city') }} />
          </Field>
          {formError ? <p className="market-add-venue-modal__error" role="alert">{formError}</p> : null}
          <div className="market-add-venue-modal__actions">
            <button type="button" className="echat-btn echat-btn--ghost" disabled={submitting} onClick={onDismiss}>Не сейчас</button>
            <button type="submit" className="echat-btn echat-btn--primary" disabled={submitting}>{submitting ? 'Сохраняем…' : 'Продолжить'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}

export const FirstOrganizationWorkspace = () => {
  const { activateOrganization } = useAuth()
  const location = useLocation()
  const [modalOpen, setModalOpen] = useState(false)
  const copy = workspaceCopyForPath(location.pathname)

  return (
    <section className="market-page market-page--wide market-first-organization">
      <header className="market-page__header">
        <h1>{copy.title}</h1>
        <p className="market-muted">{copy.lead}</p>
      </header>
      <MarketEmptyState
        title={copy.emptyTitle}
        description={copy.emptyDescription}
        actions={[{ label: copy.actionLabel, onClick: () => setModalOpen(true) }]}
      />
      <FirstOrganizationModal
        open={modalOpen}
        onDismiss={() => setModalOpen(false)}
        onCompleted={async (organization) => {
          await activateOrganization(organization)
          setModalOpen(false)
        }}
      />
    </section>
  )
}
